import _ from 'lodash';
import React, { Component } from 'react';
import { Button, Col, Row } from 'reactstrap';
import { CheckBox } from '../../../components/ui/CheckBox';
import { CustomPeriodsFilter } from '../../../components/ui/FiltersBar/components/CustomPeriodsFilter';
import { DropdownComponent } from './ColumnForm/DropdownComponent';
import { DROPDOWNS, INPUTS } from './definitions';
import {
  getMeasureOptions,
  getQuestionOptions,
  getSegmentOptions,
  getSurveysOptions,
} from './https';
import { IColumn, IColumnFormProps, IColumnFormState } from './interfaces';
import { Modal, ModalBody, Spinner } from 'reactstrap';

const DROP_DOWN_DEPENDENCIES = {
  survey: 'hierarchyLevel',
  question: 'survey',
  measure: 'question',
  segment: 'question',
};

class ColumnForm extends Component<IColumnFormProps, IColumnFormState> {
  state: IColumnFormState = {
    column: undefined,
    dropdowns: DROPDOWNS,
    activeTab: 'label',
    inputs: INPUTS,
    loadingStates: {
      period: false,
      survey: false,
      question: false,
      measure: false,
      segment: false,
    },
    selectedPeriod: '',
    periodValues: [],
    previousDropdownSelectedOptions: [],
    placeholderForPeriodDropdownMenu: 'Loading...',
  };

  constructor(props: Readonly<IColumnFormProps>) {
    super(props);
    this.state.previousDropdownSelectedOptions = [];

    if (props.column) {
      this.state.loadingStates = {
        period: true,
        survey: true,
        question: true,
        measure: true,
        segment: true,
      };
      this.state.column = {
        id: props.column.id ? props.column.id : undefined,
        name: props.column.name ? props.column.name : undefined,
        order: props.column.order ? props.column.order : undefined,
        period: props.column.period ? props.column.period : undefined,
        dateRange: props.column.dateRange ? props.column.dateRange : '',
        survey: props.column.survey ? props.column.survey : undefined,
        question: props.column.question ? props.column.question : undefined,
        measure: props.column.measure ? props.column.measure : undefined,
        segment: props.column.segment || 'all',
        addNumberOfSurveys: props.column.addNumberOfSurveys
          ? props.column.addNumberOfSurveys
          : false,
        addRankingColumn: props.column.addRankingColumn
          ? props.column.addRankingColumn
          : false,
        decimals: props.column.decimals ? props.column.decimals : undefined,
        hasBeenDeleted: props.column.hasBeenDeleted
          ? props.column.hasBeenDeleted
          : false,
      };
      if (this.state.column.dateRange.length > 0) {
        const startDateStr = this.state.column.dateRange.split('-')[0];
        const endDateStr = this.state.column.dateRange.split('-')[1];
        const startDate = new Date(
          Number(startDateStr.split('/')[2]),
          Number(startDateStr.split('/')[1]) - 1,
          Number(startDateStr.split('/')[0])
        );
        const endDate = new Date(
          Number(endDateStr.split('/')[2]),
          Number(endDateStr.split('/')[1]) - 1,
          Number(endDateStr.split('/')[0])
        );
        const startDateDay = startDate.getDate();
        const startMonth = startDate.getMonth();
        const startFullYear = startDate.getFullYear();
        const endDateDay = endDate.getDate();
        const endMonth = endDate.getMonth();
        const endFullYear = endDate.getFullYear();
        this.state.startDateDay = startDateDay;
        this.state.startMonth = startMonth;
        this.state.startFullYear = startFullYear;
        this.state.endDateDay = endDateDay;
        this.state.endMonth = endMonth;
        this.state.endFullYear = endFullYear;
      }

      this.state.selectedPeriod = props.column.period.toString();
    } else {
      this.state.column = {
        id: undefined,
        name: undefined,
        order: undefined,
        period: undefined,
        survey: undefined,
        question: undefined,
        measure: undefined,
        segment: 'all',
        addNumberOfSurveys: false,
        addRankingColumn: false,
        decimals: undefined,
        hasBeenDeleted: false,
      };
    }

    this.handleInputChange = this.handleInputChange.bind(this);
  }

  componentDidMount() {
    this.getSurveysOptionsMethod();
  }

  private getSurveysOptionsMethod() {
    if (this.props.hierarchyLevel.length > 0) {
      this.setState((prevState) => ({
        previousDropdownSelectedOptions: [
          ...prevState.previousDropdownSelectedOptions,
          'hierarchyLevel',
        ],
      }));
      const loadingStates = { ...this.state.loadingStates };
      loadingStates.survey = true;
      this.setState({ loadingStates }, () => {
        getSurveysOptions(this.props.hierarchyLevel).then((surveys) => {
          const loadingStates = { ...this.state.loadingStates };
          loadingStates.survey = false;
          const updatedDropdowns = this.state.dropdowns.map((item) => {
            if (item.name === 'survey') {
              item.options = surveys;
            }
            return item;
          });

          this.setState({ dropdowns: updatedDropdowns }, () => {
            if (this.state.column.survey) {
              this.updateDropdown('survey', this.state.column.survey);
            }
          });
          this.setState({ loadingStates });
        });
      });
    }
  }

  componentDidUpdate(
    prevProps: Readonly<IColumnFormProps>,
    prevState: Readonly<IColumnFormState>,
    snapshot?: any
  ) {
    if (this.props.column) {
      if (prevProps.column !== this.props.column) {
        const column: IColumn = {
          id: this.props.column.id ? this.props.column.id : undefined,
          name: this.props.column.name ? this.props.column.name : undefined,
          order: this.props.column.order ? this.props.column.order : undefined,
          period: this.props.column.period ? this.props.column.period : undefined,
          dateRange: this.props.column.dateRange
            ? this.props.column.dateRange
            : '',
          survey: this.props.column.survey ? this.props.column.survey : undefined,
          question: this.props.column.question
            ? this.props.column.question
            : undefined,
          measure: this.props.column.measure
            ? this.props.column.measure
            : undefined,
          segment: this.props.column.segment
            ? this.props.column.segment
            : undefined,
          addNumberOfSurveys: this.props.column.addNumberOfSurveys
            ? this.props.column.addNumberOfSurveys
            : false,
          addRankingColumn: this.props.column.addRankingColumn
            ? this.props.column.addRankingColumn
            : false,
          decimals: this.props.column.decimals
            ? this.props.column.decimals
            : undefined,
          hasBeenDeleted: this.props.column.hasBeenDeleted
            ? this.props.column.hasBeenDeleted
            : false,
        };
        this.setState({ column });
        if (column.dateRange.length > 0) {
          const startDateStr = column.dateRange.split('-')[0];
          const endDateStr = column.dateRange.split('-')[1];
          this.updateDateRange(
            new Date(
              Number(startDateStr.split('/')[2]),
              Number(startDateStr.split('/')[1]),
              Number(startDateStr.split('/')[0])
            ),
            new Date(
              Number(endDateStr.split('/')[2]),
              Number(endDateStr.split('/')[1]),
              Number(endDateStr.split('/')[0])
            )
          );
        }
      }
    } else {
      if (prevProps.column !== this.props.column) {
        const column: IColumn = {
          id: undefined,
          name: undefined,
          order: undefined,
          period: undefined,
          dateRange: '',
          survey: undefined,
          question: undefined,
          measure: undefined,
          segment: undefined,
          addNumberOfSurveys: false,
          addRankingColumn: false,
          decimals: undefined,
          hasBeenDeleted: false,
        };
        this.setState({ column });
      }
    }

    // This will reset input validation if selected column changes
    if (prevState.column.id !== this.state.column.id) {
      const { inputs } = this.state;
      inputs.forEach((v, k) => {
        inputs.set(k, true);
      });
      this.setState({ inputs });
    }

    if (prevProps.hierarchyLevel !== this.props.hierarchyLevel) {
      this.getSurveysOptionsMethod();
    }
  }

  handleInputChange(event) {
    const target = event.target;
    const value = target.type === 'checkbox' ? target.checked : target.value;
    const name = target.name;

    const partialState: any = {};
    partialState.column = this.state.column;
    partialState.column[name] = value;

    this.setState(partialState);
  }

  /**
   * Updates the selected option for the current dropdown
   * Using async/await to avoid race conditions in measure/segment fetching.
   */
  async updateDropdown(name: string, selectedOption: number | string) {
    const column = this.state.column;
    const updatedDropdowns = [...this.state.dropdowns];
    const updateIdx = updatedDropdowns.findIndex((item) => item.name === name);

    // Update local dropdown data
    updatedDropdowns[updateIdx].selectedOption = selectedOption;

    // Handle measure-format rule
    const selectedOptionObj = updatedDropdowns[updateIdx].options.find(
      (item) => item.id === selectedOption
    );
    const selectedOptionLabel = selectedOptionObj ? selectedOptionObj.label : '';
    if (selectedOptionLabel.startsWith('%') && name === 'measure') {
      column.format = '%';
      this.setState({ column });
    }

    // Update state with new dropdown selection
    this.setState({ dropdowns: updatedDropdowns });

    // Depending on which dropdown is updated, fetch next-level data
    if (name === 'survey') {
      // We need to fetch question options
      const loadingStates = { ...this.state.loadingStates };
      loadingStates.question = true;

      this.setState(
        (prevState) => ({
          loadingStates,
          previousDropdownSelectedOptions: [
            ...prevState.previousDropdownSelectedOptions,
            'survey',
          ],
        }),
        async () => {
          try {
            const questions = await getQuestionOptions(selectedOption.toString());
            const newLoadingStates = { ...this.state.loadingStates };
            newLoadingStates.question = false;

            const updatedMappedDropdowns = this.state.dropdowns.map((item) => {
              if (item.name === 'question') {
                item.options = questions;
              }
              return item;
            });

            this.setState({ dropdowns: updatedMappedDropdowns }, () => {
              if (this.state.column.question) {
                this.updateDropdown('question', this.state.column.question);
              }
            });
            this.setState({ loadingStates: newLoadingStates });
          } catch (error) {
            console.error('Error fetching question options:', error);
            this.setState({
              loadingStates: { ...this.state.loadingStates, question: false },
            });
          }
        }
      );
    }

    if (name === 'question') {
      // We need to fetch measure + segment options
      const loadingStates = { ...this.state.loadingStates };
      loadingStates.measure = true;
      loadingStates.segment = true;

      this.setState(
        (prevState) => ({
          previousDropdownSelectedOptions: [
            ...prevState.previousDropdownSelectedOptions,
            'question',
          ],
          loadingStates,
        }),
        async () => {
          const surveyDropdown = this.state.dropdowns.find(
            (dropdown) => dropdown.name === 'survey'
          );
          const survey = surveyDropdown
            ? surveyDropdown.selectedOption.toString()
            : '';

          try {
            // 1) Fetch measure options
            const measureQuestions = await getMeasureOptions(
              survey,
              selectedOption.toString()
            );
            // Update measure
            const measureLoadingStates = { ...this.state.loadingStates };
            measureLoadingStates.measure = false;

            let updatedMappedDropdowns = this.state.dropdowns.map((item) => {
              if (item.name === 'measure') {
                item.options = measureQuestions;
              }
              return item;
            });

            // If we have a saved measure in this.state.column, reselect it
            this.setState(
              {
                dropdowns: updatedMappedDropdowns,
                loadingStates: measureLoadingStates,
              },
              () => {
                if (this.state.column.measure) {
                  this.updateDropdown('measure', this.state.column.measure);
                }
              }
            );
          } catch (error) {
            console.error('Error fetching measure options:', error);
            this.setState({
              loadingStates: { ...this.state.loadingStates, measure: false },
            });
          }

          try {
            // 2) Fetch segment options
            const segmentQuestions = await getSegmentOptions(
              survey,
              selectedOption.toString()
            );

            const segmentLoadingStates = { ...this.state.loadingStates };
            segmentLoadingStates.segment = false;

            let defaultSelectedOption: string | number | undefined = undefined;
            let updatedMappedDropdowns = this.state.dropdowns.map((item) => {
              if (item.name === 'segment') {
                // If the response is empty, default to "all"
                if (!segmentQuestions || segmentQuestions.length === 0) {
                  item.options = [{ id: 'all', label: 'All Segments' }];
                  defaultSelectedOption = 'all';
                } else {
                  item.options = segmentQuestions;
                  // Check if there's an "all segments" label
                  segmentQuestions.forEach((option) => {
                    if (option.label.toLowerCase() === 'all segments') {
                      defaultSelectedOption = option.id;
                    }
                  });
                }
              }
              return item;
            });

            this.setState(
              {
                dropdowns: updatedMappedDropdowns,
                loadingStates: segmentLoadingStates,
              },
              () => {
                // If we have a default or a saved segment, set it
                if (defaultSelectedOption && !this.state.column.segment) {
                  this.updateDropdown('segment', defaultSelectedOption);
                } else if (this.state.column.segment) {
                  this.updateDropdown('segment', this.state.column.segment);
                }
              }
            );
          } catch (error) {
            console.error('Error fetching segment options:', error);
            this.setState({
              loadingStates: { ...this.state.loadingStates, segment: false },
            });
          }
        }
      );
    }

    // Finally, update column option with the new selection
    this.updateColumnOption(name, selectedOption);
  }

  /**
   * Updates column field based on dropdown selection
   * @param name
   * @param selectedOptionId
   */
  updateColumnOption(name: string, selectedOptionId: number | string) {
    const updatedColumn = { ...this.state.column };

    updatedColumn[name] = selectedOptionId;
    if (name === 'measure') {
      const measureDropdown = this.state.dropdowns.find(
        (i) => i.name === 'measure'
      );
      if (!measureDropdown) {
        return;
      }
      const selectedOption = measureDropdown.options.find(
        (option) => option.id === selectedOptionId
      );
      if (selectedOption && selectedOption.precision !== undefined) {
        updatedColumn.decimals = selectedOption.precision;
      }
    }

    this.setState({ column: updatedColumn });
  }

  toggleAddNumberOfSurveysColumn(isChecked: boolean) {
    const updatedColumn = { ...this.state.column };
    updatedColumn.addNumberOfSurveys = isChecked;
    this.setState({ column: updatedColumn });
  }

  toggleRankingColumn(isChecked: boolean) {
    const updatedColumn = { ...this.state.column };
    updatedColumn.addRankingColumn = isChecked;
    this.setState({ column: updatedColumn });
  }

  validateForm() {
    const { updateColumn } = this.props;
    const { inputs, dateRange } = this.state;
    const updatedColumn = _.cloneDeep(this.state.column);
    let isValidForm = true;

    inputs.forEach((isValid, input) => {
      // We ignore 'period' in required checks
      if (input !== 'period') {
        if (updatedColumn[input] === undefined) {
          isValidForm = false;
          inputs.set(input, false);
        } else {
          inputs.set(input, true);
        }
      }
    }, this);

    if (dateRange) {
      updatedColumn.dateRange = dateRange;
    }
    this.setState({ inputs }, () => {
      if (isValidForm) {
        updateColumn(updatedColumn.id, updatedColumn);
      }
    });
  }

  updateDateRange(startDate: Date = new Date(), endDate: Date = new Date()) {
    const startDateDay = startDate.getDate();
    const startMonth = startDate.getMonth();
    const startFullYear = startDate.getFullYear();
    const endDateDay = endDate.getDate();
    const endMonth = endDate.getMonth();
    const endFullYear = endDate.getFullYear();
    const dateRange = `${startDateDay}/${startMonth + 1}/${startFullYear}-${endDateDay}/${endMonth + 1}/${endFullYear}`;
    this.setState({
      dateRange,
      startDateDay,
      startMonth,
      startFullYear,
      endDateDay,
      endMonth,
      endFullYear,
    });
  }

  updatePeriod(periods: string[]) {
    const newSelectedPeriod = periods[periods.length - 1];
    this.setState({
      selectedPeriod: newSelectedPeriod,
      periodValues: periods,
    });
  }

  onCustomPeriodsFilterClosed() {
    const { selectedPeriod } = this.state;
    const updatedColumn = _.cloneDeep(this.state.column);
    updatedColumn.period = selectedPeriod;
    this.setState({ column: updatedColumn });
  }

  render() {
    const { togglePopover, deleteColumn } = this.props;
    const column = this.state.column;
    const {
      inputs,
      loadingStates,
      selectedPeriod,
      periodValues,
      startDateDay,
      startMonth,
      startFullYear,
      endDateDay,
      endMonth,
      endFullYear,
      previousDropdownSelectedOptions,
    } = this.state;

    let startDate = new Date();
    startDate.setDate(1);
    let endDate = new Date();
    if (
      startDateDay >= 0 &&
      startMonth >= 0 &&
      startFullYear >= 0 &&
      endDateDay >= 0 &&
      endMonth >= 0 &&
      endFullYear
    ) {
      startDate = new Date(startFullYear, startMonth, startDateDay);
      endDate = new Date(endFullYear, endMonth, endDateDay);
    }
    return (
      <div>
        <Row>
          <Col sm={6} className="margin-bottom">
            <input
              name="name"
              type="text"
              placeholder="Column Name"
              value={
                column && column.name && column.name.length > 0 ? column.name : ''
              }
              onChange={this.handleInputChange}
            />
            <span
              className={
                'text-danger ' + (inputs.get('name') ? 'd-none' : '')
              }
            >
              This is a required field
            </span>
          </Col>
          <Col sm={6} className="margin-bottom">
            <Button close onClick={() => togglePopover()} />
          </Col>
        </Row>
        <Row>
          <Col sm={6}>
            <div
              key={`custom-period-dropdown-menu`}
              className="margin-bottom"
              style={{
                border: 'black',
                borderStyle: 'solid',
                borderWidth: 'thin',
                display: 'flex',
                justifyContent: 'space-between',
                alignContent: 'center',
                alignItems: 'center',
              }}
            >
              <strong style={{ fontSize: '1rem', marginLeft: '0.7rem' }}>
                Period:{' '}
              </strong>
              <CustomPeriodsFilter
                values={periodValues}
                useLinkView={false}
                rightAlign={true}
                persistLabel={false}
                placeholder={this.props.placeholderForPeriodDropdownMenu}
                defaultValue={selectedPeriod}
                onChange={(periods) => {
                  this.updatePeriod(periods);
                }}
                onClosed={() => {
                  this.onCustomPeriodsFilterClosed();
                }}
                showMonthPicker={true}
                startDateRange={startDate}
                endDateRange={endDate}
                updateDateRange={(selectedStartDate, selectedEndDate) => {
                  this.updateDateRange(selectedStartDate, selectedEndDate);
                }}
                dateRange={''}
                disableDatePicker={false}
                disableSubmitButton={true}
                hasLoadedEvent={(hasLoaded: boolean) => {
                  if (this.props.hasLoadedEvent) {
                    this.props.hasLoadedEvent(hasLoaded);
                  }
                }}
              />
            </div>
            {this.state.dropdowns.map((dropdown) => {
              // Skip decimals & segment in the left column
              if (dropdown.name !== 'decimals') {
                return (
                  <DropdownComponent
                    key={`${dropdown.name}-dropdown-menu`}
                    updateDropdown={(dropdownName, optionID) =>
                      this.updateDropdown(dropdownName, optionID)
                    }
                    dropdown={dropdown}
                    column={column}
                    loadingStates={loadingStates}
                    inputs={inputs}
                    previousDropdownNameDependency={
                      DROP_DOWN_DEPENDENCIES[dropdown.name]
                    }
                    previousDropdownSelectedOptions={
                      previousDropdownSelectedOptions
                    }
                  />
                );
              }
            })}
          </Col>
          <Col sm={6}>
            <div className="margin-bottom">
              <CheckBox
                onChange={(check) => this.toggleAddNumberOfSurveysColumn(check)}
                isEnabled
                isChecked={column ? column.addNumberOfSurveys : false}
                label="Add # Responses Column"
              />
            </div>
            <div className="margin-bottom" hidden>
              <CheckBox
                onChange={(check) => this.toggleRankingColumn(check)}
                isEnabled
                isChecked={column ? column.addRankingColumn : false}
                label="Add Ranking Column"
              />
            </div>
            <Button
              color="primary"
              style={{ padding: '0px 30px' }}
              className="right"
              onClick={() => {
                this.validateForm();
              }}
            >
              Save
            </Button>
            <Button
              outline
              color="danger"
              style={{
                padding: '0px 10px',
                border: 'none',
                position: 'absolute',
                bottom: '0',
                right: '15px',
              }}
              className="right"
              onClick={() => {
                deleteColumn(this.state.column.id);
              }}
            >
              Delete Column
            </Button>
          </Col>
        </Row>
      </div>
    );
  }
}

export default ColumnForm;
